import React from "react";
import {
  Card,
  Button,
  Space,
  Descriptions,
  Table,
  Typography,
  Tag,
  App,
  Spin,
  Popconfirm,
  Alert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import supplierAdvanceRefundService, {
  recoveryStatusTagColor,
  refundVoucherStatusColor,
  type SupplierAdvanceRefundChargeLine,
} from "@/services/supplierAdvanceRefund.service";
import { useCurrency } from "@/contexts/CurrencyContext";
import EntityAttachmentsCard from "@/components/documents/EntityAttachmentsCard";
import {
  DOCUMENT_ENTITY_TYPE_SUPPLIER_ADVANCE_REFUND_VOUCHER,
  SCM_ATTACHMENT_UPLOAD_HINT,
} from "@/constants/document-entity-types";
import ReportDocumentActions from "@/components/finance/report-document/ReportDocumentActions";

const { Title, Text } = Typography;

const LIST_PATH = "/finance/ap/supplier-advance-refunds";

const SupplierAdvanceRefundDetailPage: React.FC = () => {
  const { t } = useTranslation("finance");
  const { t: tc } = useTranslation("common");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();

  const { data: voucher, isLoading } = useQuery({
    queryKey: ["supplier-advance-refund", id],
    queryFn: () => supplierAdvanceRefundService.findById(id!),
    enabled: !!id,
  });

  const postMutation = useMutation({
    mutationFn: () => supplierAdvanceRefundService.post(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["supplier-advance-refund", id],
      });
      queryClient.invalidateQueries({ queryKey: ["supplier-advance-refunds"] });
      message.success(t("refundVoucherPosted"));
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || t("postFailed")),
  });

  const cancelMutation = useMutation({
    mutationFn: () => supplierAdvanceRefundService.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["supplier-advance-refund", id],
      });
      queryClient.invalidateQueries({ queryKey: ["supplier-advance-refunds"] });
      message.success(t("refundVoucherCancelled"));
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || t("cancelFailed")),
  });

  if (isLoading || !voucher) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  const chargeColumns: ColumnsType<SupplierAdvanceRefundChargeLine> = [
    { title: t("type"), dataIndex: "charge_type", width: 160 },
    {
      title: t("account"),
      key: "account",
      render: (_, r) =>
        r.account
          ? `${r.account.account_code} — ${r.account.account_name}`
          : r.account_id,
    },
    {
      title: t("amount"),
      dataIndex: "amount",
      align: "right",
      width: 120,
      render: (v: number, r) =>
        formatCurrency(Number(v), r.currency ?? voucher.refund_currency),
    },
    { title: t("remarks"), dataIndex: "remarks", ellipsis: true },
  ];

  const fmtAdv = (n: number) => formatCurrency(n, voucher.advance_currency);
  const fmtRef = (n: number) => formatCurrency(n, voucher.refund_currency);
  const fmtBase = (n: number) => formatCurrency(n, voucher.base_currency);

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(LIST_PATH)}
        style={{ marginBottom: 8 }}
      >
        {t("backToList")}
      </Button>

      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
        wrap
      >
        <Space direction="vertical" size={0}>
          <Title level={4} style={{ margin: 0 }}>
            {voucher.voucher_number}
          </Title>
          <Text type="secondary">{t("supplierAdvanceRefundVoucher")}</Text>
        </Space>
        <Space wrap>
          {voucher.status === "Draft" && (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`${LIST_PATH}/${id}/edit`)}
              >
                {tc("edit")}
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={postMutation.isPending}
                onClick={() => postMutation.mutate()}
              >
                {tc("post")}
              </Button>
            </>
          )}
          <ReportDocumentActions
            reportKey="supplier-advance-refund"
            params={id ? { id } : null}
            documentId={id}
            sendPermissions={["finance.voucher.create"]}
            emailLabel={tc("email")}
          />
          {voucher.status === "Posted" && voucher.journal_voucher_id && (
            <ReportDocumentActions
              reportKey="accounting-voucher"
              params={
                id
                  ? { source_type: "supplier_advance_refund", source_id: id }
                  : null
              }
              documentId={id}
              sendPermissions={["finance.voucher.create"]}
              emailLabel={t("emailGl")}
            />
          )}
          {voucher.status !== "Cancelled" &&
            ["Draft", "Submitted", "Approved", "Posted"].includes(
              voucher.status,
            ) && (
              <Popconfirm
                title={t("cancelThisRefundVoucher")}
                description={
                  voucher.status === "Posted"
                    ? t("cancelRefundPostedDesc")
                    : t("cancelRefundDraftDesc")
                }
                onConfirm={() => cancelMutation.mutate()}
                okText={t("cancelVoucher")}
                cancelText={tc("keep")}
              >
                <Button
                  danger
                  icon={<StopOutlined />}
                  loading={cancelMutation.isPending}
                >
                  {tc("cancel")}
                </Button>
              </Popconfirm>
            )}
        </Space>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label={tc("status")}>
            <Tag color={refundVoucherStatusColor(voucher.status)}>
              {tc(voucher.status.toLowerCase())}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("refundDate")}>
            {voucher.refund_date
              ? dayjs(voucher.refund_date).format("DD MMM YYYY")
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("reference")}>
            {voucher.reference_number ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("supplier")}>
            {voucher.supplier?.supplier_name ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("po")}>
            {voucher.purchaseOrder?.po_number ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("advanceRequest")}>
            {voucher.supplierAdvanceRequest?.advance_request_number ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("receivingAccount")}>
            {voucher.receivingAccount
              ? `${voucher.receivingAccount.account_code} — ${voucher.receivingAccount.account_name}`
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("advanceGl")}>
            {voucher.advanceGlAccount
              ? `${voucher.advanceGlAccount.account_code} — ${voucher.advanceGlAccount.account_name}`
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("postedLabel")}>
            {voucher.posted_at
              ? dayjs(voucher.posted_at).format("DD MMM YYYY HH:mm")
              : "—"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t("recoveryAmounts")} style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={3}>
          <Descriptions.Item label={t("originalAdvance")}>
            {fmtAdv(Number(voucher.original_advance_amount))}
          </Descriptions.Item>
          <Descriptions.Item label={t("alreadyRefunded")}>
            {fmtAdv(Number(voucher.already_refunded))}
          </Descriptions.Item>
          <Descriptions.Item label={t("alreadyAdjusted")}>
            {fmtAdv(Number(voucher.already_adjusted))}
          </Descriptions.Item>
          <Descriptions.Item label={t("refundAvailable")}>
            {fmtAdv(Number(voucher.refund_available))}
          </Descriptions.Item>
          <Descriptions.Item label={t("thisRefund")}>
            {fmtAdv(Number(voucher.refund_amount))}
          </Descriptions.Item>
          <Descriptions.Item label={t("remainingBalance")}>
            <Text strong>{fmtAdv(Number(voucher.remaining_balance))}</Text>
          </Descriptions.Item>
          <Descriptions.Item label={t("bankReceived")}>
            {fmtRef(Number(voucher.bank_received_amount))}
          </Descriptions.Item>
          <Descriptions.Item label={t("baseEquivalent")}>
            {fmtBase(Number(voucher.base_equivalent))}
          </Descriptions.Item>
          <Descriptions.Item label={t("exchangeDifference")}>
            <Text
              type={
                Number(voucher.exchange_difference) < 0 ? "danger" : "success"
              }
            >
              {fmtBase(Number(voucher.exchange_difference))}
            </Text>
          </Descriptions.Item>
        </Descriptions>
        {voucher.narration && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={t("narration")}
            description={voucher.narration}
          />
        )}
      </Card>

      {(voucher.chargeLines?.length ?? 0) > 0 && (
        <Card title={t("chargesDeductions")} style={{ marginBottom: 16 }}>
          <Table
            rowKey={(r, idx) => r.id ?? `cl-${idx}`}
            size="small"
            pagination={false}
            dataSource={voucher.chargeLines}
            columns={chargeColumns}
          />
        </Card>
      )}

      <Card
        title={t("exchangeRates")}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Descriptions size="small" column={3}>
          <Descriptions.Item label={t("advanceCurrency")}>
            {voucher.advance_currency}
          </Descriptions.Item>
          <Descriptions.Item label={t("refundCurrency")}>
            {voucher.refund_currency}
          </Descriptions.Item>
          <Descriptions.Item label={t("baseCurrency")}>
            {voucher.base_currency}
          </Descriptions.Item>
          <Descriptions.Item label={t("advanceRate")}>
            {Number(voucher.advance_exchange_rate ?? 1).toFixed(6)}
          </Descriptions.Item>
          <Descriptions.Item label={t("refundRate")}>
            {Number(voucher.refund_exchange_rate ?? 1).toFixed(6)}
          </Descriptions.Item>
          <Descriptions.Item label={t("recoveryStatus")}>
            {voucher.remaining_balance <= 0 ? (
              <Tag color={recoveryStatusTagColor("Fully Refunded")}>
                {t("fullyRefunded")}
              </Tag>
            ) : (
              <Tag color={recoveryStatusTagColor("Partially Refunded")}>
                {t("partiallyRefunded")}
              </Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <EntityAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER_ADVANCE_REFUND_VOUCHER}
        entityId={voucher.id}
        title={tc("attachments")}
        readOnly={voucher.status === "Cancelled"}
        queryKeyPrefix={["documents", "supplier-advance-refund"]}
        uploadHint={SCM_ATTACHMENT_UPLOAD_HINT}
      />
    </div>
  );
};

export default SupplierAdvanceRefundDetailPage;
